import { useEffect, useMemo, useRef } from "react";
import {
  EARTH_AXIAL_TILT_DEGREES,
  LUNAR_ORBIT_INCLINATION_DEGREES,
  SYNODIC_MONTH_DAYS,
} from "../simulation/constants";
import { useSimulation } from "../simulation/SimulationProvider";
import { getMoonPosition, phaseIndexToDay } from "../simulation/geometry";
import { PHASE_NAMES } from "../simulation/types";
import type { Vector3 } from "../simulation/types";
import { drawPhaseDisk } from "./phaseDisk";

const VIEWBOX_WIDTH = 800;
const VIEWBOX_HEIGHT = 460;
const EARTH_CENTRE = { x: 440, y: 230 };
const ORBIT_SAMPLE_COUNT = 193;
const PHASE_DISK_SIZE = 160;
const MOON_ROTATION_MARKER_OFFSET = 10;

interface ProjectedPoint {
  readonly x: number;
  readonly y: number;
}

function projectMoonPosition(position: Vector3): ProjectedPoint {
  return {
    x: EARTH_CENTRE.x + position[0] * 34,
    y: EARTH_CENTRE.y - position[2] * 16 - position[1] * 24,
  };
}

function getEarthFacingMarkerPosition(
  moonPosition: ProjectedPoint,
): ProjectedPoint {
  const towardEarthX = EARTH_CENTRE.x - moonPosition.x;
  const towardEarthY = EARTH_CENTRE.y - moonPosition.y;
  const distanceToEarth = Math.hypot(towardEarthX, towardEarthY);

  if (distanceToEarth === 0) {
    throw new RangeError("Projected Moon position must differ from Earth");
  }

  return {
    x:
      moonPosition.x +
      (towardEarthX / distanceToEarth) * MOON_ROTATION_MARKER_OFFSET,
    y:
      moonPosition.y +
      (towardEarthY / distanceToEarth) * MOON_ROTATION_MARKER_OFFSET,
  };
}

const ORBIT_POINTS = Array.from({ length: ORBIT_SAMPLE_COUNT }, (_, index) =>
  projectMoonPosition(
    getMoonPosition((index / (ORBIT_SAMPLE_COUNT - 1)) * SYNODIC_MONTH_DAYS),
  ),
);
const ORBIT_PATH = ORBIT_POINTS.map(
  (point, index) => `${index === 0 ? "M" : "L"}${point.x},${point.y}`,
).join(" ");
const PHASE_POINTS = PHASE_NAMES.map((_, index) =>
  projectMoonPosition(getMoonPosition(phaseIndexToDay(index))),
);
const SUNLIGHT_RAY_Y = [126, 178, 230, 282, 334] as const;

function PhaseDisk() {
  const { snapshot } = useSimulation();
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (canvas.current !== null) {
      drawPhaseDisk(canvas.current, snapshot, PHASE_DISK_SIZE);
    }
  }, [snapshot]);

  return (
    <div
      aria-label={`Analytically shaded ${snapshot.phaseName}`}
      className="earth-view-slot fallback-earth-view"
      data-testid="earth-view-slot"
      role="img"
    >
      <canvas aria-hidden="true" ref={canvas} />
      <span>{snapshot.phaseName}</span>
    </div>
  );
}

export function FallbackExhibit() {
  const { selectPhase, snapshot, state } = useSimulation();
  const activeMoon = useMemo(
    () => projectMoonPosition(snapshot.moonPosition),
    [snapshot.moonPosition],
  );
  const moonRotationMarker = useMemo(
    () => getEarthFacingMarkerPosition(activeMoon),
    [activeMoon],
  );

  return (
    <div className="fallback-exhibit">
      <div
        aria-label="Interactive 2D Sun, Earth, and Moon orbital geometry"
        className="fallback-diagram"
        role="group"
      >
        <div className="fallback-projection">
          <svg
            aria-label="Projected Sun, Earth, and Moon orbit"
            className="fallback-orbit-svg"
            role="img"
            viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
          >
            <defs>
              <marker
                id="fallback-arrowhead"
                markerHeight="8"
                markerWidth="10"
                orient="auto"
                refX="8"
                refY="4"
                viewBox="0 0 10 8"
              >
                <path d="M0 0 L10 4 L0 8 Z" fill="#ffd166" />
              </marker>
            </defs>

            <g aria-label="Sun direction">
              <rect
                className="fallback-sun"
                height="300"
                rx="36"
                width="100"
                x="-34"
                y="80"
              />
              <text className="fallback-sun-label" x="24" y="54">
                Sun direction
              </text>
            </g>

            {state.guides.sunlightRays ? (
              <g aria-label="Parallel sunlight rays">
                {SUNLIGHT_RAY_Y.map((y) => (
                  <line
                    key={y}
                    className="fallback-sunlight-ray"
                    data-testid="sunlight-ray"
                    markerEnd="url(#fallback-arrowhead)"
                    x1="74"
                    x2="748"
                    y1={y}
                    y2={y}
                  />
                ))}
              </g>
            ) : null}

            {state.guides.orbitalPlanes ? (
              <g aria-label="Orbital plane guides">
                <line
                  className="fallback-ecliptic"
                  x1="132"
                  x2="748"
                  y1={EARTH_CENTRE.y}
                  y2={EARTH_CENTRE.y}
                />
                <path className="fallback-orbit-plane" d={ORBIT_PATH} />
                <text className="fallback-angle-label" x="468" y="166">
                  Earth axial tilt: {EARTH_AXIAL_TILT_DEGREES}°
                </text>
                <text className="fallback-angle-label" x="468" y="316">
                  Moon orbit inclination: {LUNAR_ORBIT_INCLINATION_DEGREES}°
                </text>
              </g>
            ) : null}

            <path
              className="fallback-orbit"
              d={ORBIT_PATH}
              data-sample-count={ORBIT_SAMPLE_COUNT}
            />

            {state.guides.allPhasePositions
              ? PHASE_POINTS.map((point, index) =>
                  index === snapshot.phaseIndex ? null : (
                    <circle
                      key={PHASE_NAMES[index]}
                      className="fallback-reference-moon"
                      cx={point.x}
                      cy={point.y}
                      data-testid="reference-moon"
                      r="9"
                    />
                  ),
                )
              : null}

            <g aria-label="Earth">
              <circle
                className="fallback-earth"
                cx={EARTH_CENTRE.x}
                cy={EARTH_CENTRE.y}
                r="37"
              />
              <line
                className="fallback-earth-equator"
                x1="406"
                x2="474"
                y1="244"
                y2="216"
              />
              <line
                className="fallback-earth-axis"
                x1="416"
                x2="464"
                y1="285"
                y2="175"
              />
              {state.guides.earthRotation ? (
                <path
                  className="fallback-earth-rotation"
                  d="M410 204 A38 38 0 0 1 472 203"
                  data-testid="earth-rotation-guide"
                  markerEnd="url(#fallback-arrowhead)"
                />
              ) : null}
            </g>

            <g
              aria-label={`Active Moon: ${snapshot.phaseName}`}
              data-testid="active-moon"
            >
              <circle
                className="fallback-active-moon"
                cx={activeMoon.x}
                cy={activeMoon.y}
                r="13"
              />
              {state.guides.moonRotationMarker ? (
                <circle
                  className="fallback-moon-rotation-marker"
                  cx={moonRotationMarker.x}
                  cy={moonRotationMarker.y}
                  data-testid="moon-rotation-marker"
                  r="3"
                />
              ) : null}
            </g>

            {state.guides.phaseLabels ? (
              <g aria-label="Phase labels">
                {PHASE_POINTS.map((point, index) => (
                  <text
                    key={PHASE_NAMES[index]}
                    className="fallback-phase-label"
                    data-testid="phase-label"
                    textAnchor="middle"
                    x={point.x}
                    y={point.y + (point.y < EARTH_CENTRE.y ? -29 : 35)}
                  >
                    {PHASE_NAMES[index]}
                  </text>
                ))}
              </g>
            ) : null}
          </svg>

          <div className="fallback-phase-markers">
            {PHASE_POINTS.map((point, index) => {
              const phaseName = PHASE_NAMES[index];
              if (phaseName === undefined) {
                return null;
              }

              const isActive = snapshot.phaseIndex === index;
              return (
                <button
                  key={phaseName}
                  type="button"
                  aria-current={isActive ? "true" : undefined}
                  aria-label={`Select ${phaseName} on orbit`}
                  className="fallback-phase-marker"
                  onClick={() => selectPhase(index, "shortest")}
                  style={{
                    left: `${(point.x / VIEWBOX_WIDTH) * 100}%`,
                    top: `${(point.y / VIEWBOX_HEIGHT) * 100}%`,
                  }}
                >
                  {isActive ? (
                    <span
                      aria-hidden="true"
                      className="fallback-marker-selected-cue"
                    >
                      ◆
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <p className="fallback-disclosure">
        This device is using the interactive 2D view. Phase geometry and
        calculations remain accurate; free 3D camera movement and textured
        spheres are unavailable.
      </p>
      <PhaseDisk />
    </div>
  );
}
